import moment from 'moment';
import store from '@/store';
import roleConstantList from '@/shared/constants/role';
import { parseJwt, isGenerateFreePlanActive } from '@/shared/functions';
import { getDetail as getDetailUserInfo } from '@/services/user.service';
import { getGeneratePlanConceptSettingApi } from '@/services/project.service';
import constants from '@/shared/constants/constants.js';

function isCurrentUserGenerateFreePlanActive() {
  return isGenerateFreePlanActive(store.state.authUser, store.getters.isUserPlanGenerate);
}

async function getAuthUser() {
  if (!store.state.isAuthenticated) {
    const { status, data } = await getDetailUserInfo();
    if (status === 200) {
      if (data.is_active !== 1) {
        store.commit('clearAuthInfo');
      } else {
        store.commit('setAuthUser', {
          authUser: data,
          isAuthenticated: true,
        });
      }
    } else {
      store.commit('setAuthUser', {
        isAuthenticated: true,
      });
    }
  }
}

export function nextFactory(context, middleware, index) {
  const subsequentMiddleware = middleware[index];

  if (!subsequentMiddleware) {
    return context.next;
  }

  return (...parameters) => {
    context.next(...parameters);

    const nextMiddleware = nextFactory(context, middleware, index + 1);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
}

export async function parseJwtMiddleware({ next }) {
  const token = localStorage.getItem('token');

  if (token) {
    const userInfo = parseJwt(token);
    const expiredAt = moment(userInfo.exp * 1000).valueOf();
    const now = moment().valueOf();

    if (expiredAt <= now) {
      store.commit('clearAuthInfo');
    } else if (!store.state.isAuthenticated) {
      store.commit('setAuthUser', {
        authUser: {
          ...userInfo,
          id: userInfo.user_id,
          expired_at: userInfo.expired_at * 1000,
          plan: {
            id: userInfo.plan,
            max_images: userInfo.max_images,
          },
          country: userInfo.country,
        },
        isAuthenticated: false,
      });
    }
  }

  return next();
}

export async function authMiddleware({ next, router }) {
  await getAuthUser();
  if (!store.state.authUser || !store.state.authUser.id || (!store.getters.isAdmin && !store.getters.isSuperAdmin && !store.getters.isUser)) {
    return next({ name: 'Login' });
  }

  return next();
}

export async function preventUserTrialWithoutData({ to, next, router }) {
  // Prevent change the plan due to the plan is trial, and user refresh the page make the user data in params lost
  const userData = to.params.user;
  if (
    store.state.authUser &&
    store.state.authUser.plan &&
    store.state.authUser.plan.plan_type === constants.PLAN_TYPE_TRIAL &&
    !userData
  ) {
    next(false);
    return router.push({ name: 'UpdateInfoUserTrial' });
  }
  return next();
}

export async function guestMiddleware({ next, router }) {
  if (store.state.authUser && store.state.authUser.id) {
    if (store.getters.isSuperAdmin) {
      return next({ name: 'Plug' });
    }

    if (store.getters.isAdmin) {
      return next({ name: 'MypageIndex' });
    }

    if (store.getters.isUser) {
      return next({ name: 'Users' });
    }
  }

  return next();
}

export function rolesMiddleWare(...roles) {
  return async ({ next, router }) => {
    for (const role of roles) {
      if (role === roleConstantList.superAdmin && store.getters.isSuperAdmin) {
        return next();
      }

      if (role === roleConstantList.admin && store.getters.isAdmin) {
        return next();
      }

      if (role === roleConstantList.user && store.getters.isUser) {
        return next();
      }
    }

    return next({ name: 'Login' });
  };
}

export async function validityMiddleware({ next, router }) {
  await getAuthUser();

  if (store.getters.isReachLimited) {
    return next({ name: 'Login' });
  }

  return next();
}

export async function planMiddleware({ next, router }) {
  if (store.getters.isUserNotFee) {
    return next({ name: 'SurveyIndex' });
  }
  return next();
}

export async function planOptionMiddleware({ next, router }) {
  // Generate free plan can NOT access ChatGPT via URL.
  if (!store.getters.canUseServiceChatGPT || isCurrentUserGenerateFreePlanActive()) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function canUseValuationSurveyMiddleware({ next, router }) {
  if (!store.getters.canUseServiceValuationSurvey) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function canUseConceptValuationMiddleware({ next, router }) {
  if (!store.getters.canUseServiceConceptValuation) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function generateFreePlanCreatedMiddleware({ next }) {
  await getAuthUser();

  if (!isCurrentUserGenerateFreePlanActive()) {
    return next();
  }

  const { status, data } = await getGeneratePlanConceptSettingApi(constants.PLAN_GENERATE_FREE_ID);
  if (status === constants.HTTP_OK && data) {
    return next({ name: 'Home' });
  }

  return next();
}

export async function canUseValuationShelveMiddleware({ next, router }) {
  if (!store.getters.canUseServiceValuationShelve) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function canUseGenerateKeyVisualMiddleware({ next, router }) {
  // Generate free plan can NOT access Key Visual via URL.
  if (!store.getters.canUseServiceGenerateKeyVisual || isCurrentUserGenerateFreePlanActive()) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function canUseGenerateConceptMiddleware({ next, router }) {
  if (!store.getters.canUseServiceGenerateConcept) {
    return next({ name: 'Home' });
  }
  return next();
}

export async function planUserAdhocMiddleware({ next, router }) {
  if (store.getters.isUserAdhoc && store.state.authUser.is_superuser !== constants.SUPERUSER) {
    return next({ name: 'SelectTypeProject' });
  }
  return next();
}

export async function planUserGenerateMiddleware({ next, router }) {
  // Generate free plan can NOT access Generate via URL.
  if (!store.getters.isUserPlanGenerate || isCurrentUserGenerateFreePlanActive()) {
    return next({ name: 'Home' });
  }
  return next();
}

export function accessLanguage(languages) {
  return async ({ next, router }) => {
    if (!languages.some(language => language === store.getters.getCurrentLanguage)) {
      return next({ name: 'Home' });
    }
    return next();
  };
}

export async function regionMiddleware({ next, router }) {
  const region = process.env.VUE_APP_REGION || 'ja';
  const token = localStorage.getItem('token');
  if (region === constants.REGION_CN) {
    if (token) {
      return next({ name: 'Home' });
    }
    return next({ name: 'Login' });
  }
  return next();
}

export async function serviceBlocked({ next, router }) {
  return next({ name: 'Home' });
}

export async function homeByRegion({ next, router }) {
  const region = process.env.VUE_APP_REGION || 'ja';
  if (region !== constants.REGION_CN) {
    const token = localStorage.getItem('token');
    if (token) {
      return next({ path: '/mypage' });
    }
    return next({ name: 'Login' });
  }
  return next();
}
